/**
 * usePushSubscription — owns the browser push subscription lifecycle.
 *
 * Behaviour:
 * 1. On mount with a signed-in user, if the browser already has
 *    `Notification.permission === 'granted'`, silently subscribe (or
 *    re-subscribe) and post the endpoint to the server. No prompt.
 * 2. Never auto-prompts. iOS Safari rejects `Notification.requestPermission()`
 *    when it isn't tied to a user gesture, so we expose a `subscribe()`
 *    function for components to call from a click handler instead.
 *
 * Returns:
 *   - status: 'unsupported' | 'denied' | 'default' | 'granted'
 *   - subscribe(): triggers the browser permission prompt + subscribes;
 *     must be called from a user gesture (button onClick).
 *   - isWorking: true while the permission/subscribe round-trip is in flight.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { trpc } from '../lib/trpc.js';
import type { Auth } from '../lib/auth.js';

const VAPID_PUBLIC_KEY = import.meta.env['VITE_VAPID_PUBLIC_KEY'] as string | undefined;

export type NotificationOptInStatus = 'unsupported' | 'denied' | 'default' | 'granted';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function currentStatus(): NotificationOptInStatus {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (typeof Notification === 'undefined') return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'unsupported';
  return Notification.permission as NotificationOptInStatus;
}

export function usePushSubscription(auth: Auth): {
  status: NotificationOptInStatus;
  isWorking: boolean;
  subscribe: () => Promise<NotificationOptInStatus>;
} {
  const [status, setStatus] = useState<NotificationOptInStatus>(currentStatus);
  const [isWorking, setIsWorking] = useState(false);
  const subscribed = useRef(false);
  const subscribeMutation = trpc.notification.subscribePush.useMutation();

  // Push endpoints + keys → notification.subscribePush. Pure subscribe,
  // assumes permission is already granted.
  const persistSubscription = useCallback(async () => {
    if (!VAPID_PUBLIC_KEY) return;
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
      }));
    const json = subscription.toJSON() as {
      endpoint: string;
      keys?: { p256dh?: string; auth?: string };
    };
    if (json.endpoint && json.keys?.p256dh && json.keys?.auth) {
      await subscribeMutation.mutateAsync({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      subscribed.current = true;
    }
  }, [subscribeMutation]);

  // Quiet auto-subscribe when permission is already granted — covers the
  // common case of a returning user who opted in previously.
  useEffect(() => {
    if (auth.kind === 'anonymous') return;
    if (subscribed.current) return;
    const s = currentStatus();
    setStatus(s);
    if (s !== 'granted') return;
    persistSubscription().catch((err) => {
      console.warn('[push] auto re-subscribe failed:', err);
    });
  }, [auth.kind, persistSubscription]);

  // User-gesture-triggered opt-in. Required on iOS Safari PWAs.
  const subscribe = useCallback(async (): Promise<NotificationOptInStatus> => {
    if (auth.kind === 'anonymous') return 'default';
    const s = currentStatus();
    if (s === 'unsupported' || s === 'denied') {
      setStatus(s);
      return s;
    }
    setIsWorking(true);
    try {
      const permission = await Notification.requestPermission();
      const next = permission as NotificationOptInStatus;
      setStatus(next);
      if (next === 'granted') {
        await persistSubscription();
      }
      return next;
    } catch (err) {
      console.warn('[push] subscribe failed:', err);
      return 'default';
    } finally {
      setIsWorking(false);
    }
  }, [auth.kind, persistSubscription]);

  return { status, isWorking, subscribe };
}
